import React, { createContext, useContext, useState, useCallback } from 'react';
import AlertDialog from 'components/AlertDialog';

const AlertContext = createContext(null);

export const AlertProvider = ({ children }) => {
  const [alert, setAlert] = useState(null);

  const hide = useCallback(() => {
    setAlert((a) => (a ? { ...a, open: false } : a));
    // allow Transition to run before unmount — but only clear an alert that
    // is still closed. After confirm() resolves, the caller often opens the
    // result alert within these 200ms (a fast API), and an unconditional
    // null here would wipe it before anyone saw it.
    setTimeout(() => setAlert((a) => (a && !a.open ? null : a)), 200);
  }, []);

  const showAlert = useCallback(
    (options) => {
      const wrap = (action) =>
        action
          ? {
              ...action,
              onClick: (...args) => {
                action.onClick?.(...args);
                hide();
              },
            }
          : null;
      setAlert({
        open: true,
        type: options.type || 'info',
        title: options.title || '',
        message: options.message || '',
        canDismiss: options.canDismiss !== false,
        primaryAction: wrap(options.primaryAction),
        secondaryAction: wrap(options.secondaryAction),
        onClose: options.onClose || null,
      });
    },
    [hide]
  );

  const confirm = useCallback(
    (options = {}) => {
      return new Promise((resolve) => {
        const onConfirm = () => {
          resolve(true);
          hide();
        };
        const onCancel = () => {
          resolve(false);
          hide();
        };

        setAlert({
          open: true,
          type: options.type || 'confirm',
          title: options.title || 'Are you sure?',
          message: options.message || '',
          canDismiss: options.canDismiss !== false,
          primaryAction: {
            label: options.confirmLabel || 'Confirm',
            onClick: () => onConfirm(),
            intent: options.confirmIntent || 'primary',
          },
          secondaryAction: {
            label: options.cancelLabel || 'Cancel',
            onClick: () => onCancel(),
            intent: 'ghost',
          },
        });
      });
    },
    [hide]
  );

  return (
    <AlertContext.Provider value={{ showAlert, confirm, hide }}>
      {children}
      <AlertDialog
        open={!!alert?.open}
        type={alert?.type}
        title={alert?.title}
        message={alert?.message}
        canDismiss={alert?.canDismiss}
        onClose={() => {
          alert?.onClose?.();
          hide();
        }}
        primaryAction={alert?.primaryAction}
        secondaryAction={alert?.secondaryAction}
      />
    </AlertContext.Provider>
  );
};

export const useAlert = () => {
  const ctx = useContext(AlertContext);
  if (!ctx) throw new Error('useAlert must be used within AlertProvider');
  return ctx;
};
